import { Check, Minus } from 'lucide-react';
import { Fragment } from 'react';
import { Link } from 'react-router';

/** A plan as the public plans list gives it (#710), with what the comparison needs (#717). */
export interface PublicPlan {
  id: string;
  name: string;
  description: string;
  monthlyCents: number;
  yearlyCents: number;
  trialDays: number;
  recommended: boolean;
  features: string[];
  maxAdults: number | null;
  maxChildren: number | null;
  childAddon: { amount: number; monthlyCents: number; maxQuantity: number } | null;
}

export const dollars = (cents: number) => `$${cents % 100 ? (cents / 100).toFixed(2) : cents / 100}`;

/** The plan features in the groups the Features guide uses, with words for families. Others land in Extras. */
const GROUPS: { name: string; rows: { key: string; label: string; hint: string }[] }[] = [
  {
    name: 'Plan & teach',
    rows: [
      { key: 'lesson_plans', label: 'Lesson plans', hint: 'Lesson plans in the planner' },
      { key: 'journal', label: 'Journal', hint: 'The homeschool journal' },
      { key: 'reading_log', label: 'Reading log', hint: 'Reading entries and sign-off' },
      { key: 'student_portal', label: 'Student Portal', hint: 'Your child’s own sign-in on the web' },
      { key: 'todo', label: 'To-do list', hint: 'Your own to-do list' },
    ],
  },
  {
    name: 'Grades & records',
    rows: [
      { key: 'grade_book', label: 'Grade book', hint: 'Graded work, categories and your grading scale' },
      { key: 'report_cards', label: 'Report cards', hint: 'Report cards and progress reports' },
      { key: 'portfolio', label: 'Portfolio', hint: 'Work samples and the year-end portfolio PDF' },
      { key: 'attendance', label: 'Attendance', hint: 'Attendance hours, estimates and reports' },
      { key: 'state_compliance', label: 'State compliance', hint: 'Your state’s requirement, deadline reminders and filled-in state documents' },
      { key: 'reports', label: 'Activity reports', hint: 'Reports, website history exports and report emails' },
    ],
  },
  {
    name: 'Focused school time',
    rows: [
      { key: 'computer', label: 'Child’s computer', hint: 'The app or extension on their computer: school time, website and app limits and breaks' },
      { key: 'live_view', label: 'Live view', hint: 'See the screen and camera, hear and talk' },
      { key: 'interact', label: 'Interact', hint: 'Control the mouse and keyboard from live view' },
      { key: 'whiteboard', label: 'Whiteboard', hint: 'A shared whiteboard' },
      { key: 'chat', label: 'Chat', hint: 'Messages with each child' },
      { key: 'app_blocking', label: 'App blocking', hint: 'Block categories and single apps' },
    ],
  },
];

function children(p: PublicPlan) {
  if (p.maxChildren === null) return 'No limit';
  const a = p.childAddon;
  if (!a) return `Up to ${p.maxChildren}`;
  const most = a.maxQuantity > 0 ? ` up to ${p.maxChildren + a.amount * a.maxQuantity}` : '';
  return `${p.maxChildren} included, +${dollars(a.monthlyCents)} each${most}`;
}

const NUMBER_WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];

/**
 * "Five or more children? Every plan has a Large Family version." (#835): from where the plans stop, the smallest
 * child limit plus one, so it follows Admin → Plans instead of being written in.
 */
export function largeFamilyLine(plans: PublicPlan[]): string {
  const caps = plans.map((p) => p.maxChildren).filter((n): n is number => n !== null && n > 0);
  if (!caps.length) return 'Every plan has a Large Family version.';
  const from = Math.min(...caps) + 1;
  return `${NUMBER_WORDS[from] ?? from} or more children? Every plan has a Large Family version.`;
}

/**
 * Compare the plans (#717): the open plans side by side, with what's included as rows of checks, straight from each
 * plan's features in Admin. The recommended plan's column is shaded and has the main button.
 */
export function ComparePlans({ plans: all, largeFamily, catalog, open }: { plans: PublicPlan[]; largeFamily: boolean; catalog: { key: string; label: string }[]; open: boolean }) {
  // The recommended plan in the middle, as on the home page.
  const rec = all.find((p) => p.recommended);
  const others = all.filter((p) => p !== rec);
  const plans = rec ? [...others.slice(0, 1), rec, ...others.slice(1)] : all;
  const offered = new Set(plans.flatMap((p) => p.features));
  const grouped = new Set(GROUPS.flatMap((g) => g.rows.map((r) => r.key)));
  const extras = catalog.filter((f) => offered.has(f.key) && !grouped.has(f.key)).map((f) => ({ key: f.key, label: f.label, hint: '' }));
  const groups = [...GROUPS, ...(extras.length ? [{ name: 'Extras', rows: extras }] : [])]
    .map((g) => ({ ...g, rows: g.rows.filter((r) => offered.has(r.key)) }))
    .filter((g) => g.rows.length);
  const trials = new Set(plans.map((p) => p.trialDays));
  const trial = trials.size === 1 && plans[0]?.trialDays ? `Every plan starts with a ${plans[0].trialDays}-day free trial.` : '';
  const shade = (p: PublicPlan) => (p.recommended ? 'compare-rec' : '');
  const cta = (p: PublicPlan) => (!open ? 'Request access' : p.recommended && p.trialDays > 0 ? 'Start your free trial' : 'Get started');

  return (
    <>
      <section className="landing-page-head">
        <h1>Compare the plans</h1>
        <p className="landing-lead">
          {trial} {largeFamily && largeFamilyLine(plans)}
        </p>
      </section>
      {plans.length === 0 ? (
        <p className="muted center">The plans are loading…</p>
      ) : (
        <div className="compare-wrap card">
          <table className="compare">
            <thead>
              <tr>
                <th className="compare-what">What’s included</th>
                {plans.map((p) => (
                  <th key={p.id} className={shade(p)}>
                    <span className="compare-plan">
                      {p.recommended && <span className="landing-plan-badge">Recommended</span>}
                      <strong>{p.name}</strong>
                      <span>
                        <strong className="compare-price">{dollars(p.monthlyCents)}</strong> <span className="muted compare-per">/ month</span>
                      </span>
                      <span className="muted small compare-terms">
                        {[p.yearlyCents > 0 && `or ${dollars(p.yearlyCents)} a year`, p.trialDays > 0 && `${p.trialDays}-day free trial`].filter(Boolean).join(' · ')}
                      </span>
                      <Link className={`btn small compare-head-btn ${p.recommended ? 'primary' : ''}`} to="/signup">
                        {cta(p)}
                      </Link>
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="compare-group">
                <td colSpan={plans.length + 1}>Your family</td>
              </tr>
              <tr>
                <td>
                  <strong>Children</strong>
                </td>
                {plans.map((p) => (
                  <td key={p.id} className={`small ${shade(p)}`}>
                    {children(p)}
                  </td>
                ))}
              </tr>
              <tr>
                <td>
                  <strong>Adults</strong>
                </td>
                {plans.map((p) => (
                  <td key={p.id} className={`small ${shade(p)}`}>
                    {p.maxAdults === null ? 'No limit' : `Up to ${p.maxAdults}`}
                  </td>
                ))}
              </tr>
              <tr>
                <td>
                  <strong>Planner &amp; calendar</strong>
                  <span className="muted small block">Assignments, due dates and the calendar</span>
                </td>
                {plans.map((p) => (
                  <td key={p.id} className={shade(p)}>
                    <Check size={20} className="compare-yes" aria-label="Included" />
                  </td>
                ))}
              </tr>
              {groups.map((g) => (
                <Fragment key={g.name}>
                  <tr className="compare-group">
                    <td colSpan={plans.length + 1}>{g.name}</td>
                  </tr>
                  {g.rows.map((r) => (
                    <tr key={r.key}>
                      <td>
                        <strong>{r.label}</strong>
                        {r.hint && <span className="muted small block">{r.hint}</span>}
                      </td>
                      {plans.map((p) => (
                        <td key={p.id} className={shade(p)}>
                          {p.features.includes(r.key) ? (
                            <Check size={20} className="compare-yes" aria-label="Included" />
                          ) : (
                            <Minus size={18} className="compare-no" aria-label="Not included" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
            {/* On a phone (#857) the plan headings shrink to a name and price, so the buttons move under the table. */}
            <tfoot className="compare-foot">
              <tr>
                <td />
                {plans.map((p) => (
                  <td key={p.id} className={shade(p)}>
                    <Link className={`btn small ${p.recommended ? 'primary' : ''}`} to="/signup" aria-label={`${cta(p)}: ${p.name}`}>
                      {open ? 'Start' : 'Ask'}
                    </Link>
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  );
}
